"use client";

import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Stack,
  Fade,
  Grid,
  Divider,
} from "@mui/material";
import {
  Link as LinkIcon,
  ContentCopy,
  Delete,
  CheckCircle,
  Code,
} from "@mui/icons-material";

type QueryParam = {
  key: string;
  value: string;
};

type ParsedResult = {
  normalizedUrl: string;
  origin: string;
  protocol: string;
  host: string;
  hostname: string;
  port: string;
  pathname: string;
  search: string;
  hash: string;
  username: string;
  password: string;
  queryParams: QueryParam[];
  pathSegments: string[];
};

function hasScheme(value: string): boolean {
  return /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(value);
}

type UrlParserProps = {
  initialInput?: string;
};

function normalizeInputFromPath(pathname: string): string {
  if (!pathname.startsWith("/url-parser/")) return "";
  const tail = pathname.slice("/url-parser/".length);
  if (!tail) return "";

  let decoded = tail;
  try {
    decoded = decodeURIComponent(tail);
  } catch {
    decoded = tail;
  }

  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:\/(?!\/)/.test(decoded)) {
    return decoded.replace(/:\/(?!\/)/, "://");
  }

  return decoded;
}

export default function UrlParser({ initialInput = "" }: UrlParserProps) {
  const [input, setInput] = useState(initialInput);
  const [parsed, setParsed] = useState<ParsedResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [autoAddedScheme, setAutoAddedScheme] = useState(false);
  const syncTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setInput(initialInput);
  }, [initialInput]);

  useEffect(() => {
    if (initialInput || input) return;
    if (typeof window === "undefined") return;

    const fromPath = normalizeInputFromPath(window.location.pathname);
    if (!fromPath) return;

    setInput(fromPath);
  }, [initialInput, input]);

  useEffect(() => {
    if (syncTimeout.current) {
      clearTimeout(syncTimeout.current);
    }

    syncTimeout.current = setTimeout(() => {
      const trimmed = input.trim();
      if (typeof window === "undefined") return;

      // Keep the page shareable without triggering a Next.js navigation.
      const targetPath = trimmed
        ? `/url-parser/${encodeURIComponent(trimmed)}`
        : "/url-parser";
      if (window.location.pathname !== targetPath) {
        window.history.replaceState(null, "", targetPath);
      }

      // Auto-parse for a smoother experience. If it fails, keep the UI quiet
      // (user can press Parse to see an error).
      if (!trimmed) return;
      const candidate = hasScheme(trimmed) ? trimmed : `https://${trimmed}`;
      const schemeAdded = !hasScheme(trimmed);
      try {
        const url = new URL(candidate);
        const queryParams = Array.from(url.searchParams.entries()).map(
          ([key, value]) => ({ key, value })
        );
        const pathSegments = url.pathname.split("/").filter(Boolean);
        setParsed({
          normalizedUrl: url.toString(),
          origin: url.origin,
          protocol: url.protocol,
          host: url.host,
          hostname: url.hostname,
          port: url.port,
          pathname: url.pathname,
          search: url.search,
          hash: url.hash,
          username: url.username,
          password: url.password,
          queryParams,
          pathSegments,
        });
        setAutoAddedScheme(schemeAdded);
        setError(null);
      } catch {
        // no-op
      }
    }, 300);

    return () => {
      if (syncTimeout.current) {
        clearTimeout(syncTimeout.current);
      }
    };
  }, [input]);

  const handleParse = () => {
    setError(null);
    setAutoAddedScheme(false);

    const text = input.trim();
    if (!text) {
      setError("Please enter a URL to parse.");
      setParsed(null);
      return;
    }

    const candidate = hasScheme(text) ? text : `https://${text}`;
    if (!hasScheme(text)) {
      setAutoAddedScheme(true);
    }

    try {
      const url = new URL(candidate);

      const queryParams = Array.from(url.searchParams.entries()).map(
        ([key, value]) => ({ key, value })
      );
      const pathSegments = url.pathname.split("/").filter(Boolean);

      setParsed({
        normalizedUrl: url.toString(),
        origin: url.origin,
        protocol: url.protocol,
        host: url.host,
        hostname: url.hostname,
        port: url.port,
        pathname: url.pathname,
        search: url.search,
        hash: url.hash,
        username: url.username,
        password: url.password,
        queryParams,
        pathSegments,
      });
    } catch {
      setError("Invalid URL. Please check the format and try again.");
      setParsed(null);
    }
  };

  const handleCopyJson = async () => {
    if (!parsed) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(parsed, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Failed to copy to clipboard");
    }
  };

  const handleCopyUrl = async () => {
    if (!parsed) return;
    try {
      await navigator.clipboard.writeText(parsed.normalizedUrl);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch {
      setError("Failed to copy to clipboard");
    }
  };

  const handleClear = () => {
    setInput("");
    setParsed(null);
    setError(null);
    setCopied(false);
    setCopiedUrl(false);
    setAutoAddedScheme(false);
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", "/url-parser");
    }
  };

  return (
    <Box sx={{ maxWidth: 980, mx: "auto" }}>
      <Paper
        elevation={8}
        sx={{
          p: 4,
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(10px)",
        }}
      >
        {/* Input */}
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          URL
        </Typography>
        <TextField
          fullWidth
          placeholder="https://example.com/path?utm=source#section"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setError(null);
            if (parsed) setParsed(null);
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              fontFamily: "monospace",
              fontSize: "0.95rem",
            },
          }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Tip: If you paste a URL without a scheme, we will assume https://
        </Typography>

        {autoAddedScheme && !error && (
          <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
            Added https:// to your input before parsing.
          </Typography>
        )}

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}

        {/* Actions */}
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          sx={{ mt: 3 }}
          flexWrap="wrap"
        >
          <Button
            variant="contained"
            size="large"
            startIcon={<LinkIcon sx={{ color: "#ffffff" }} />}
            onClick={handleParse}
            disabled={!input.trim()}
            sx={{
              py: 1.5,
              px: 4,
              fontSize: "1rem",
              fontWeight: 600,
              background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
              boxShadow: "0 3px 15px rgba(102, 126, 234, 0.3)",
              "&:hover": {
                background: "linear-gradient(45deg, #5568d3 30%, #63408b 90%)",
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
              },
              "&.Mui-disabled": {
                background: "#e0e0e0",
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            Parse URL
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<ContentCopy />}
            onClick={handleCopyUrl}
            disabled={!parsed}
            sx={{ py: 1.5, px: 3, fontWeight: 600 }}
          >
            Copy URL
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<ContentCopy />}
            onClick={handleCopyJson}
            disabled={!parsed}
            sx={{ py: 1.5, px: 3, fontWeight: 600 }}
          >
            Copy JSON
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<Delete />}
            onClick={handleClear}
            sx={{ py: 1.5, px: 3, fontWeight: 600 }}
          >
            Clear
          </Button>
        </Stack>

        {/* Copied notifications */}
        <Fade in={copied || copiedUrl}>
          <Box
            sx={{
              mt: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <CheckCircle color="success" />
            <Typography color="success.main" fontWeight={600}>
              {copied ? "Copied JSON to clipboard" : "Copied URL to clipboard"}
            </Typography>
          </Box>
        </Fade>

        {parsed && (
          <Box sx={{ mt: 3 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2.25,
                borderRadius: 1,
                background:
                  "linear-gradient(135deg, rgba(102, 126, 234, 0.14), rgba(118, 75, 162, 0.12))",
                border: "1px solid rgba(102, 126, 234, 0.18)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  mb: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Parsed Components
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Structured, readable, and ready to copy.
                  </Typography>
                </Box>
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 8,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "primary.main",
                    bgcolor: "rgba(102, 126, 234, 0.12)",
                    border: "1px solid rgba(102, 126, 234, 0.25)",
                  }}
                >
                  {parsed.protocol.replace(":", "").toUpperCase()}
                </Box>
              </Box>

              <Grid container spacing={1.5}>
                {[
                  { label: "Host", value: parsed.host },
                  { label: "Pathname", value: parsed.pathname || "/" },
                  { label: "Search", value: parsed.search || "—" },
                  { label: "Hash", value: parsed.hash || "—" },
                ].map((item) => (
                  <Grid key={item.label} size={{ xs: 12, md: 6 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.25,
                        borderRadius: 0.75,
                        background: "rgba(255,255,255,0.9)",
                        border: "1px solid rgba(102, 126, 234, 0.18)",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}
                        color="text.secondary"
                      >
                        {item.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.5,
                          fontFamily: "monospace",
                          wordBreak: "break-all",
                        }}
                      >
                        {item.value}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Box
                sx={{
                  mt: 2,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 1.5,
                }}
              >
                {[
                  { label: "Normalized URL", value: parsed.normalizedUrl },
                  { label: "Origin", value: parsed.origin },
                  { label: "Hostname", value: parsed.hostname },
                  { label: "Port", value: parsed.port || "—" },
                  { label: "Username", value: parsed.username || "—" },
                  {
                    label: "Password",
                    value: parsed.password ? "••••••" : "—",
                  },
                ].map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      p: 1.25,
                      borderRadius: 0.75,
                      background: "rgba(255,255,255,0.75)",
                      border: "1px dashed rgba(102, 126, 234, 0.2)",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}
                      color="text.secondary"
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        fontFamily: "monospace",
                        wordBreak: "break-all",
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Divider
                sx={{
                  my: 2,
                  borderColor: "transparent",
                  height: 2,
                  background:
                    "linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.45), transparent)",
                }}
              />

              <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.75,
                      borderRadius: 1,
                      height: "100%",
                      background: "rgba(255,255,255,0.9)",
                      border: "1px solid rgba(102, 126, 234, 0.2)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Code color="primary" />
                      <Typography variant="subtitle2" fontWeight={600}>
                        Query Parameters ({parsed.queryParams.length})
                      </Typography>
                    </Box>
                    {parsed.queryParams.length === 0 ? (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        No query parameters found.
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          mt: 1.5,
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                        }}
                      >
                        {parsed.queryParams.map((param, index) => (
                          <Box
                            key={`${param.key}-${index}`}
                            sx={{
                              px: 1.25,
                              py: 0.75,
                              borderRadius: 999,
                              bgcolor: "rgba(102, 126, 234, 0.08)",
                              border: "1px solid rgba(102, 126, 234, 0.2)",
                              fontFamily: "monospace",
                              fontSize: "0.85rem",
                            }}
                          >
                            {param.key} = {param.value || "(empty)"}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.75,
                      borderRadius: 1,
                      height: "100%",
                      background: "rgba(255,255,255,0.9)",
                      border: "1px solid rgba(102, 126, 234, 0.2)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Code color="primary" />
                      <Typography variant="subtitle2" fontWeight={600}>
                        Path Segments ({parsed.pathSegments.length})
                      </Typography>
                    </Box>
                    {parsed.pathSegments.length === 0 ? (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        No path segments found.
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          mt: 1.5,
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                        }}
                      >
                        {parsed.pathSegments.map((segment, index) => (
                          <Box
                            key={`${segment}-${index}`}
                            sx={{
                              px: 1.25,
                              py: 0.75,
                              borderRadius: 999,
                              bgcolor: "rgba(118, 75, 162, 0.08)",
                              border: "1px solid rgba(118, 75, 162, 0.2)",
                              fontFamily: "monospace",
                              fontSize: "0.85rem",
                            }}
                          >
                            {segment}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
